import { z } from 'zod';
import { moneySchema } from '../money';
import { projectHealthSchema } from '../projects/project-summary.schema';

/** Länge der Monatsreihe im Umsatz-Chart (ältester Monat zuerst, laufender Monat zuletzt). */
export const DASHBOARD_REVENUE_MONTHS = 12;

/** Ein Monat der Umsatzreihe: ISO-Monat (JJJJ-MM, Europe/Berlin) + fakturierter Netto-Umsatz. */
export const dashboardRevenueMonthSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month muss JJJJ-MM sein'),
  net: moneySchema,
});
export type DashboardRevenueMonth = z.infer<typeof dashboardRevenueMonthSchema>;

/**
 * Kennzahlen des Start-Dashboards, serverseitig über ALLE Datensätze gerechnet
 * (`GET /v1/dashboard/kpis`, ADR 0062). Früher zählte der Browser die erste
 * Listenseite (≤ 200 Zeilen) — ab dem 201. Datensatz waren die Kacheln falsch.
 *
 * `null` heißt „für dich nicht freigegeben", NIE „0": Der Server füllt jedes
 * Feld nur, wenn der Kontext die zugehörigen Rechte hat (Kachel ausblenden statt
 * „0,00 €" zeigen). Beträge in Mikro-Einheiten (CLAUDE.md §6); wie bisher im
 * Dashboard währungsübergreifend summiert und als EUR ausgewiesen.
 */
export const dashboardKpisSchema = z.object({
  /** Stichtag der Berechnung (UTC). Jahr/Monate zählen in Europe/Berlin. */
  asOf: z.string().datetime(),
  /** Fakturierter Netto-Umsatz — `dashboard.viewfinancials` UND `sales.read`. */
  revenue: z
    .object({
      /** Laufendes Kalenderjahr (Europe/Berlin). */
      year: z.number().int(),
      currentYearNet: moneySchema,
      previousYearNet: moneySchema,
      /** Die letzten `DASHBOARD_REVENUE_MONTHS` Monate inkl. laufendem, ältester zuerst. */
      monthly: z.array(dashboardRevenueMonthSchema),
    })
    .nullable(),
  /** Offene Angebote — `sales.read`; das Volumen zusätzlich `dashboard.viewfinancials`. */
  openQuotes: z
    .object({
      count: z.number().int().nonnegative(),
      /** Summe brutto; `null` ohne `dashboard.viewfinancials`. */
      grossVolume: moneySchema.nullable(),
    })
    .nullable(),
  /** Laufende Projekte (nicht archiviert, nicht geschlossen/storniert) — `projects.read`. */
  activeProjects: z.number().int().nonnegative().nullable(),
  /** Einkaufsbelege ohne Bestellpool — `procurement.read`. `open` = Entwurf/bestellt. */
  purchaseOrders: z
    .object({
      open: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    })
    .nullable(),
  /** Artikel mit gepflegtem Mindestbestand darunter (ohne Ablängungen) — `inventory.read`. */
  articlesBelowMinStock: z.number().int().nonnegative().nullable(),
  /**
   * Projektgesundheit über ALLE laufenden Projekte (2026-09-29; vorher die ersten
   * 200 der Projektliste) — `projects.read`. Auftragswerte der kritischen
   * Projekte zusätzlich nur mit `dashboard.viewfinancials` und dem
   * Auftragswert-Recht der Projektliste, sonst `null`.
   */
  projectHealth: projectHealthSchema.nullable(),
});
export type DashboardKpis = z.infer<typeof dashboardKpisSchema>;
